import { create } from "zustand";
import { CampaignFrontend, ReviewFrontend, ReviewSegmentFrontend } from "../syncedTypes";

interface CampaignStore {
  selectedCampaign: CampaignFrontend | null;
  allCampaigns: CampaignFrontend[];
  setSelectedCampaign: (campaign: CampaignFrontend) => void;
  setCampaigns: (campaigns: CampaignFrontend[]) => void;
  setCampaignToViewAll: () => void;
  updateCampaign: (campaign: CampaignFrontend) => void;
  deleteCampaign: (campaignId: number) => void;
}

const eachSegIsTheSameSource = (segs1: ReviewSegmentFrontend[], segs2: ReviewSegmentFrontend[]) => {
  for (let i = 0; i < segs1.length; i++) {
    if (segs1[i].videoURL.split('?')[0] === segs2[i].videoURL.split('?')[0]) {
      continue;
    } else {
      return false;
    }
  }
  return true;
}

const zipperCampaign = (newCampaign: CampaignFrontend, oldCampaign: CampaignFrontend) => {
  const oldReviewsMap: {[key: number]: ReviewFrontend} = {}

  oldCampaign.reviews.forEach(el => oldReviewsMap[el.id] = el);

  const zippered: ReviewFrontend[] = [];

  newCampaign.reviews.forEach(el => {
    if (oldReviewsMap[el.id] && eachSegIsTheSameSource(oldReviewsMap[el.id].segments, el.segments)) {
      zippered.push(oldReviewsMap[el.id]);
    } else {
      zippered.push(el);
    }
  });

  newCampaign.reviews = zippered;

  return newCampaign;
}

const useCampaigns = create<CampaignStore>((set) => ({
  selectedCampaign: null,
  allCampaigns: [],
  setSelectedCampaign: (campaign: CampaignFrontend) =>
    set((state) => {
      if (state.selectedCampaign?.id === campaign.id) {
        return {selectedCampaign: zipperCampaign(campaign, state.selectedCampaign as CampaignFrontend)};
      } else {
        return { selectedCampaign: campaign };
      }
    }),
  setCampaigns: (campaigns: CampaignFrontend[]) =>
    set((state) => {
      const normalizedCampaigns = campaigns.map(el => {
        // This is a workaround for a crash where a question was not correctly recorded
        // Consider removing in the future
        el.reviews = el.reviews.filter(el => el.segments?.length);
        el.reviews.forEach(el => {
          el.segments = el.segments.filter(el => !!el)
        })
        if (el.id === state.selectedCampaign?.id) {
          return zipperCampaign(el, state.selectedCampaign);
        } else {
          return el;
        }
      })
      return { allCampaigns: normalizedCampaigns };
    }),
  setCampaignToViewAll: () =>
    set((state) => {
      const reviews: ReviewFrontend[] = [];
      state.allCampaigns.forEach((el) => {
        el.reviews.forEach((el) => {
          reviews.push(el);
        });
      });
      reviews.sort((a, b) => b.dateRecorded - a.dateRecorded);
      return {
        selectedCampaign: {
          id: 0,
          hash: "",
          dateCreated: 0,
          isActive: false,
          questions: [],
          name: "All Reviews",
          reviews: reviews,
        },
      };
    }),
  updateCampaign: (campaign: CampaignFrontend) => {
    set((state) => {
      const allCurrentCampaigns = [...state.allCampaigns];
      const indexOfCampaignToUpdate = allCurrentCampaigns.findIndex(
        (el) => el.id === campaign.id
      );
      if (indexOfCampaignToUpdate === -1) {
        allCurrentCampaigns.unshift(campaign);
      } else {
        allCurrentCampaigns[indexOfCampaignToUpdate] = zipperCampaign(campaign, allCurrentCampaigns[indexOfCampaignToUpdate]);
      }
      return {
        allCampaigns: allCurrentCampaigns,
        selectedCampaign: indexOfCampaignToUpdate === -1 ? campaign : zipperCampaign(campaign, allCurrentCampaigns[indexOfCampaignToUpdate])
      };
    });
  },
  deleteCampaign: (campaignId: number) => {
    set((state) => {
      const allCurrentCampaigns = [...state.allCampaigns];
      const filtered = allCurrentCampaigns.filter((el) => el.id !== campaignId);
      return {
        allCampaigns: filtered,
        selectedCampaign: filtered[0],
      };
    });
  },
}));

export default useCampaigns;
